import { useState } from "react";
import { Printer, CreditCard, FileText } from "lucide-react";
import { openPrintCard } from "@/lib/printCard";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";

// A reusable "print & carry" call-to-action: a picture, a short description, and two
// print buttons (pocket holy-card, 2 per page · and a full page). Optionally offers a
// language selector when `langs` + `buildCard` are supplied.
export const PrintableCard = ({ card, buildCard, langs, testid, eyebrow, heading, description }) => {
  const [error, setError] = useState("");
  const [lang, setLang] = useState((langs && langs[0]?.code) || "en");
  const resolve = () => (buildCard ? buildCard(lang) : card);
  const image = (card || (buildCard && buildCard(langs?.[0]?.code || "en"))).image;
  const print = (layout) => setError(openPrintCard(resolve(), layout));
  return (
    <section data-testid={testid} className="mx-auto w-full min-w-0 max-w-7xl px-4 pb-16 sm:px-8 lg:px-16">
      <div className="printable-card-layout grid min-w-0 grid-cols-[minmax(0,1fr)] items-center gap-8 rounded-3xl border border-gold/40 bg-paleblue/30 p-6 sm:p-10 md:grid-cols-[auto_minmax(0,1fr)]">
        <figure className="mx-auto flex w-full min-w-0 max-w-[12rem] items-center justify-center overflow-hidden rounded-2xl border border-marian/15 bg-gradient-to-b from-white to-paleblue/40 p-3 shadow-[0_18px_40px_-30px_rgba(16,42,67,0.5)] sm:max-w-[14rem]">
          <img src={image.src} alt={image.alt} className="max-h-72 w-auto max-w-full rounded-lg object-contain" />
        </figure>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{eyebrow}</p>
          <h2 className="mt-2 font-serif text-2xl font-semibold text-midnight sm:text-3xl">{heading}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-charcoal/85 sm:text-base">{description}</p>
          {langs && (
            <div className="mt-5" data-testid={`${testid}-langs`}>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slateblue">Print language</p>
              <div className="flex flex-wrap gap-2">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLang(l.code)}
                    data-testid={`${testid}-lang-${l.code}`}
                    aria-pressed={lang === l.code}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${lang === l.code ? "border-marian bg-marian text-white" : "border-marian/25 text-marian hover:border-gold"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <PrayerAudio prayerId={resolve().audioId || testid} prayerTitle={resolve().title} prefix={testid} lang={lang} />
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => print("card")}
              data-testid={`${testid}-print-card`}
              className="group inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-marian px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-midnight"
            >
              <CreditCard className="h-4 w-4 shrink-0" />
              <span className="min-w-0">Print holy card <span className="font-normal text-white/80">· 2 per page</span></span>
            </button>
            <button
              type="button"
              onClick={() => print("page")}
              data-testid={`${testid}-print-page`}
              className="group inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border border-marian/30 px-5 py-3 text-sm font-semibold text-marian transition-colors hover:border-gold hover:text-midnight"
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="min-w-0">Print full page</span>
            </button>
          </div>
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slateblue"><Printer className="h-3 w-3" /> Holy card prints 2 per page with a matching back (shrine name & blessing) — print double-sided. Allow pop-ups if nothing appears.</p>
          {error && <p role="alert" data-testid={`${testid}-error`} className="mt-2 text-sm text-red-800">{error}</p>}
        </div>
      </div>
    </section>
  );
};
